import { useState, useEffect } from 'react';
import featureFlagService from '../../services/featureFlagService';

const FeatureManagementPage = () => {
  const [featuresByCategory, setFeaturesByCategory] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = () => {
    const grouped = featureFlagService.getFeaturesByCategory();
    setFeaturesByCategory(grouped);
  };

  const handleToggle = (featureKey) => {
    featureFlagService.toggleFeature(featureKey);
    loadFeatures();
    setSaveMessage('Feature updated successfully');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleEnableAll = (category) => {
    const features = featuresByCategory[category];
    features.forEach(feature => {
      if (!feature.alwaysEnabled) {
        featureFlagService.enableFeature(feature.key);
      }
    });
    loadFeatures();
    setSaveMessage(`All ${category} features enabled`);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleDisableAll = (category) => {
    const features = featuresByCategory[category];
    features.forEach(feature => {
      if (!feature.alwaysEnabled) {
        featureFlagService.disableFeature(feature.key);
      }
    });
    loadFeatures();
    setSaveMessage(`All ${category} features disabled`);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleReset = () => {
    featureFlagService.resetFeatureFlags();
    loadFeatures();
    setShowResetConfirm(false);
    setSaveMessage('All features reset to default state');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const getFilteredFeatures = () => {
    let allFeatures = [];
    Object.keys(featuresByCategory).forEach(category => {
      if (selectedCategory === 'all' || selectedCategory === category) {
        allFeatures = [...allFeatures, ...featuresByCategory[category]];
      }
    });

    if (searchTerm) {
      allFeatures = allFeatures.filter(feature =>
        feature.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        feature.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return allFeatures;
  };

  const categories = Object.keys(featuresByCategory);
  const filteredFeatures = getFilteredFeatures();

  const getCategoryStats = (category) => {
    const features = featuresByCategory[category] || [];
    const enabled = features.filter(f => f.enabled).length;
    const total = features.length;
    return { enabled, total };
  };

  return (
    <div className="container-xl">
      <div className="page-header d-print-none">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title">Broker Feature Management</h2>
            <div className="text-secondary mt-1">
              Control which features and tabs are visible to Broker users
            </div>
          </div>
          <div className="col-auto ms-auto">
            <button 
              className="btn btn-outline-danger"
              onClick={() => setShowResetConfirm(true)}
            >
              <i className="ti ti-refresh me-1"></i>
              Reset All to Default
            </button>
          </div>
        </div>
      </div>

      {saveMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <div className="d-flex">
            <div>
              <i className="ti ti-check icon alert-icon"></i>
            </div>
            <div>
              {saveMessage}
            </div>
          </div>
        </div>
      )}

      {/* Category Summary Cards */}
      <div className="row mb-4">
        {categories.map(category => {
          const stats = getCategoryStats(category);
          return (
            <div key={category} className="col-md-3 mb-3">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="subheader">{category}</div>
                  </div>
                  <div className="h1 mb-0 mt-2">{stats.enabled}/{stats.total}</div>
                  <div className="text-secondary">Features Enabled</div>
                  <div className="progress progress-sm mt-2">
                    <div 
                      className="progress-bar bg-primary" 
                      style={{ width: `${(stats.enabled / stats.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="input-icon">
                <span className="input-icon-addon">
                  <i className="ti ti-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search features..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6">
              <select
                className="form-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Features by Category */}
      {selectedCategory === 'all' ? (
        categories.map(category => (
          <div key={category} className="card mb-3">
            <div className="card-header">
              <h3 className="card-title">{category}</h3>
              <div className="card-actions">
                <button 
                  className="btn btn-sm btn-primary me-2"
                  onClick={() => handleEnableAll(category)}
                >
                  Enable All
                </button>
                <button 
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => handleDisableAll(category)}
                >
                  Disable All
                </button>
              </div>
            </div>
            <div className="list-group list-group-flush">
              {featuresByCategory[category]?.map(feature => (
                <div key={feature.key} className="list-group-item">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <span className="avatar">
                        <i className={`ti ${feature.icon}`}></i>
                      </span>
                    </div>
                    <div className="col">
                      <div className="fw-bold">{feature.name}</div>
                      <div className="text-secondary small">{feature.description}</div>
                      {feature.alwaysEnabled && (
                        <span className="badge bg-blue-lt mt-1">Always Enabled</span>
                      )}
                    </div>
                    <div className="col-auto">
                      <label className="form-check form-switch m-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={feature.enabled}
                          onChange={() => handleToggle(feature.key)}
                          disabled={feature.alwaysEnabled}
                        />
                        <span className="form-check-label">
                          {feature.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">{selectedCategory}</h3>
            <div className="card-actions">
              <button 
                className="btn btn-sm btn-primary me-2"
                onClick={() => handleEnableAll(selectedCategory)}
              >
                Enable All
              </button>
              <button 
                className="btn btn-sm btn-outline-secondary"
                onClick={() => handleDisableAll(selectedCategory)}
              >
                Disable All
              </button>
            </div>
          </div>
          <div className="list-group list-group-flush">
            {filteredFeatures.map(feature => (
              <div key={feature.key} className="list-group-item">
                <div className="row align-items-center">
                  <div className="col-auto">
                    <span className="avatar">
                      <i className={`ti ${feature.icon}`}></i>
                    </span>
                  </div>
                  <div className="col">
                    <div className="fw-bold">{feature.name}</div>
                    <div className="text-secondary small">{feature.description}</div>
                    {feature.alwaysEnabled && (
                      <span className="badge bg-blue-lt mt-1">Always Enabled</span>
                    )}
                  </div>
                  <div className="col-auto">
                    <label className="form-check form-switch m-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={feature.enabled}
                        onChange={() => handleToggle(feature.key)}
                        disabled={feature.alwaysEnabled}
                      />
                      <span className="form-check-label">
                        {feature.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowResetConfirm(false)}
              ></button>
              <div className="modal-status bg-danger"></div>
              <div className="modal-body text-center py-4">
                <i className="ti ti-alert-triangle icon mb-2 text-danger icon-lg"></i>
                <h3>Reset All Features?</h3>
                <div className="text-secondary">
                  This will reset all feature flags to their default state. Most features will be disabled.
                </div>
              </div>
              <div className="modal-footer">
                <div className="w-100">
                  <div className="row">
                    <div className="col">
                      <button
                        className="btn w-100"
                        onClick={() => setShowResetConfirm(false)}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="col">
                      <button
                        className="btn btn-danger w-100"
                        onClick={handleReset}
                      >
                        Reset All
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showResetConfirm && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default FeatureManagementPage;
